/* eslint-disable camelcase */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { clsx } from 'clsx'
import { z } from 'zod'

import styles from './FlowStatePage.module.scss'
import baseStyles from '@/pages/base.module.scss'

import { isApiError } from '@/api'
import {
  FlowStatePreviewRequest,
  FlowStateProfilePreviewItem,
  FlowStateProfilePreviewResponse,
  generateAndSendFlowStateEmails,
  generateFlowStateProfiles,
  GenerateFlowStateRequest,
  getFlowStateJob,
  getFlowStateMonthSummary,
  MarketingJobStatusResponse,
  previewFlowStateProfiles,
  sendFlowStateEmails,
  SendFlowStateRequest,
} from '@/api/internal'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import DataTable from '@/components/DataTable'
import Input from '@/components/Input'
import TextArea from '@/components/TextArea'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'

const FEATURE_PATH_FALLBACK = '/app/home'
const POLL_INTERVAL_MS = 2000
const JOB_TIMEOUT_MS = 30 * 60_000
const PREVIEW_PAGE_SIZE = 100
const ALL_USERS_CONFIRM_TEXT = 'SEND'
const FLOW_STATE_MONTH_QUERY_KEY = 'flow-state-month'
const AUDIENCE_MODE_STORAGE_KEY = 'flow-state-audience-mode'

type AudienceMode = 'emails' | 'domain' | 'all'
type Progress = Pick<MarketingJobStatusResponse, 'stage' | 'total' | 'succeeded' | 'failed' | 'status'>
type SendResult = { sent: number; failed: number; total: number }
type WaitForJobOptions = {
  signal?: AbortSignal
  timeoutMs?: number
  terminalStage?: string
}

const monthToReportingDate = (yyyyMm: string): string => `${yyyyMm}-01`

const profileNoun = (count: number): string => (count === 1 ? 'profile' : 'profiles')

const readStoredAudienceMode = (): AudienceMode => {
  try {
    const value = window.localStorage.getItem(AUDIENCE_MODE_STORAGE_KEY)
    return value === 'emails' || value === 'domain' || value === 'all' ? value : 'emails'
  } catch {
    return 'emails'
  }
}

const storeAudienceMode = (mode: AudienceMode) => {
  try {
    window.localStorage.setItem(AUDIENCE_MODE_STORAGE_KEY, mode)
  } catch {
    // Ignore storage failures; backend month summary remains the progress source of truth.
  }
}

const firstOfPreviousMonth = (): string => {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`
}

const parseEmails = (text: string): string[] =>
  Array.from(
    new Set(
      text
        .split(/[\s,;]+/)
        .map((s) => s.trim().replace(/^<|>$/g, '').toLowerCase())
        .filter(Boolean)
    )
  )

const invalidEmail = (emails: string[]): string | undefined =>
  emails.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))

const normalizeDomain = (value: string): string => value.trim().replace(/^@/, '').toLowerCase()

const flowStateFormSchema = z
  .object({
    audienceMode: z.enum(['emails', 'domain', 'all']),
    reportingMonth: z.string().regex(/^\d{4}-\d{2}$/, 'Choose a reporting month.'),
    emailsText: z.string(),
    domainText: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.audienceMode === 'emails') {
      const emails = parseEmails(values.emailsText)
      if (emails.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['emailsText'],
          message: 'Enter at least one email address.',
        })
        return
      }
      const bad = invalidEmail(emails)
      if (bad) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['emailsText'],
          message: `Invalid email: ${bad}`,
        })
      }
    }

    if (values.audienceMode === 'domain') {
      const domain = normalizeDomain(values.domainText)
      if (!/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/.test(domain)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['domainText'],
          message: 'Enter a domain like example.com.',
        })
      }
    }
  })

type FlowStateFormValues = z.infer<typeof flowStateFormSchema>

const errorMessage = (err: unknown): string => {
  if (isApiError(err)) {
    if (err.status === 409) return 'A Flow State job for this month is already running.'
    if (err.status >= 500) return 'The backend could not start or complete this Flow State job.'
    return err.detail || err.message
  }
  return err instanceof Error ? err.message : String(err)
}

const waitForJob = async (
  jobId: string,
  onProgress: (status: MarketingJobStatusResponse) => void,
  options: WaitForJobOptions = {}
): Promise<MarketingJobStatusResponse> => {
  const deadline = Date.now() + (options.timeoutMs ?? JOB_TIMEOUT_MS)
  let consecutiveFailures = 0
  while (Date.now() < deadline) {
    if (options.signal?.aborted) throw new DOMException('Flow State job polling was cancelled.', 'AbortError')
    try {
      const { data } = await getFlowStateJob(jobId)
      consecutiveFailures = 0
      onProgress(data)
      if (data.status === 'failed') return data
      if (data.status === 'completed' && (!options.terminalStage || data.stage === options.terminalStage)) return data
    } catch (err) {
      if (options.signal?.aborted) throw err
      consecutiveFailures += 1
      if (consecutiveFailures > 3) throw err
    }
    await new Promise((resolve, reject) => {
      const timeout = window.setTimeout(resolve, POLL_INTERVAL_MS)
      options.signal?.addEventListener(
        'abort',
        () => {
          window.clearTimeout(timeout)
          reject(new DOMException('Flow State job polling was cancelled.', 'AbortError'))
        },
        { once: true }
      )
    })
  }
  throw new Error('Timed out waiting for Flow State job to finish. It may still be running in the backend.')
}

const ProgressBlock = ({ progress }: { progress: Progress }) => {
  const done = progress.succeeded + progress.failed
  const percent = progress.total > 0 ? Math.round((done / progress.total) * 100) : 0
  const label = progress.stage === 'send' ? 'Sending events' : 'Generating profiles'

  return (
    <div className={styles.progress} role="status" aria-live="polite">
      <div className={styles.progressLabel}>
        <span>{label}</span>
        <span>
          {done} / {progress.total > 0 ? progress.total : '...'}
        </span>
      </div>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${percent}%` }} />
      </div>
      {progress.stage !== 'send' && (
        <span className={styles.progressHint}>Progress updates after each 10,000 generated profiles.</span>
      )}
      {progress.failed > 0 && <span className={styles.progressFailed}>{progress.failed} failed</span>}
    </div>
  )
}

const buildAudiencePayload = (
  mode: AudienceMode,
  reportingMonth: string,
  emailsText: string,
  domainText: string
): GenerateFlowStateRequest & SendFlowStateRequest => {
  const reporting_month = monthToReportingDate(reportingMonth)
  if (mode === 'emails') {
    const emails = parseEmails(emailsText)
    return { reporting_month, emails }
  }

  if (mode === 'domain') {
    const domain = normalizeDomain(domainText)
    return { reporting_month, domain }
  }

  return { reporting_month, all_users: true }
}

const previewColumnHelper = createColumnHelper<FlowStateProfilePreviewItem>()

const PreviewTable = ({
  preview,
  page,
  onPage,
  isLoading,
}: {
  preview: FlowStateProfilePreviewResponse | null
  page: number
  onPage: (page: number) => void
  isLoading: boolean
}) => {
  const columns = useMemo(
    () => [
      previewColumnHelper.accessor('email', { header: 'Email' }),
      previewColumnHelper.accessor('eligibility_word_count', {
        header: 'Words',
      }),
      previewColumnHelper.accessor('send_status', { header: 'Send status' }),
      previewColumnHelper.accessor('send_attempts', { header: 'Attempts' }),
      previewColumnHelper.display({
        id: 'profile',
        header: 'Profile JSON',
        cell: ({ row }) => (
          <details className={styles.profileDetails}>
            <summary>View</summary>
            <pre>{JSON.stringify(row.original.profile, null, 2)}</pre>
          </details>
        ),
      }),
    ],
    []
  )

  const total = preview?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PREVIEW_PAGE_SIZE))

  return (
    <div className={styles.previewBlock}>
      <div className={styles.previewHeader}>
        <div>
          <h3 className={styles.sectionTitle}>Generated profiles</h3>
          <p className={styles.sectionHint}>
            {total} generated {total === 1 ? 'profile' : 'profiles'}
          </p>
        </div>
        <div className={styles.pageControls}>
          <Button
            title="Previous"
            type="light"
            onClick={() => onPage(Math.max(0, page - 1))}
            isDisabled={page === 0 || isLoading}
          />
          <span>
            {page + 1} / {totalPages}
          </span>
          <Button
            title="Next"
            type="light"
            onClick={() => onPage(Math.min(totalPages - 1, page + 1))}
            isDisabled={page >= totalPages - 1 || isLoading}
          />
        </div>
      </div>
      <DataTable
        data={preview?.items ?? []}
        columns={columns}
        isLoading={isLoading}
        emptyMessage="No generated profiles."
        ariaLabel="Generated Flow State profiles"
        manualPagination
      />
    </div>
  )
}

const FlowStatePage = () => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const queryClient = useQueryClient()
  const [initialMode] = useState(readStoredAudienceMode)
  const [mode, setMode] = useState<AudienceMode>(initialMode)
  const [defaultReportingMonth] = useState(firstOfPreviousMonth)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [progress, setProgress] = useState<Progress | null>(null)
  const [preview, setPreview] = useState<FlowStateProfilePreviewResponse | null>(null)
  const [previewPage, setPreviewPage] = useState(0)
  const [sendResult, setSendResult] = useState<SendResult | null>(null)
  const [allUsersQueuedMessage, setAllUsersQueuedMessage] = useState<string | null>(null)
  const [pendingAllUsersEnqueueMonth, setPendingAllUsersEnqueueMonth] = useState<string | null>(null)
  const [allUsersConfirmOpen, setAllUsersConfirmOpen] = useState(false)
  const [allUsersConfirmValue, setAllUsersConfirmValue] = useState('')
  const [pendingAllUsersMonth, setPendingAllUsersMonth] = useState<string | null>(null)
  const pollAbortRef = useRef<AbortController | null>(null)
  const { control, getFieldState, setValue, trigger } = useForm<FlowStateFormValues>({
    resolver: zodResolver(flowStateFormSchema),
    defaultValues: {
      audienceMode: initialMode,
      reportingMonth: defaultReportingMonth,
      emailsText: '',
      domainText: '',
    },
  })
  const values = useWatch({ control })
  const reportingMonth = values.reportingMonth ?? defaultReportingMonth
  const emailsText = values.emailsText ?? ''
  const domainText = values.domainText ?? ''
  const reportingDate = monthToReportingDate(reportingMonth)

  const monthSummaryQuery = useQuery({
    queryKey: [FLOW_STATE_MONTH_QUERY_KEY, reportingDate],
    queryFn: () => getFlowStateMonthSummary(reportingDate).then(({ data }) => data),
    enabled: mode === 'all',
    refetchInterval: (query) => (query.state.data?.active_job ? POLL_INTERVAL_MS : false),
  })
  const monthSummary = monthSummaryQuery.data
  const activeAllUsersJob = mode === 'all' ? monthSummary?.active_job : null
  const allUsersSendableCount =
    (monthSummary?.pending_send_total ?? 0) +
    (monthSummary?.sending_total ?? 0) +
    (monthSummary?.failed_send_total ?? 0)
  const allUsersProgress: Progress | null = activeAllUsersJob
    ? {
        status: activeAllUsersJob.status,
        stage: activeAllUsersJob.stage ?? null,
        total: activeAllUsersJob.total,
        succeeded: activeAllUsersJob.succeeded,
        failed: activeAllUsersJob.failed,
      }
    : null

  useEffect(() => {
    return () => pollAbortRef.current?.abort()
  }, [])

  useEffect(() => {
    if (pendingAllUsersEnqueueMonth !== reportingDate) return
    if (monthSummaryQuery.isFetching || !monthSummary) return
    setPendingAllUsersEnqueueMonth(null)
  }, [monthSummary, monthSummaryQuery.isFetching, pendingAllUsersEnqueueMonth, reportingDate])

  useEffect(() => {
    if (mode !== 'all' || !allUsersQueuedMessage || !monthSummary) return
    if (monthSummary.active_job || ['completed', 'failed'].includes(monthSummary.latest_job?.status ?? '')) {
      setAllUsersQueuedMessage(null)
    }
  }, [allUsersQueuedMessage, mode, monthSummary])

  const clearAudienceResults = () => {
    setPreview(null)
    setSendResult(null)
    setAllUsersQueuedMessage(null)
  }

  const setFormValue = (field: keyof FlowStateFormValues, value: string) => {
    setValue(field, value, { shouldDirty: true })
    clearAudienceResults()
  }

  const firstFormError = (): string =>
    getFieldState('reportingMonth').error?.message ||
    getFieldState('emailsText').error?.message ||
    getFieldState('domainText').error?.message ||
    'Check the highlighted fields.'

  const resolveAudiencePayload = async (): Promise<(GenerateFlowStateRequest & SendFlowStateRequest) | null> => {
    const valid = await trigger()
    if (!valid) {
      setValidationError(firstFormError())
      return null
    }
    return buildAudiencePayload(mode, reportingMonth, emailsText, domainText)
  }

  const onProgress = (data: MarketingJobStatusResponse) => {
    setProgress((current) => ({
      status: data.status,
      stage: data.stage ?? current?.stage ?? null,
      total: data.total,
      succeeded: data.succeeded,
      failed: data.failed,
    }))
  }

  const createPollController = () => {
    pollAbortRef.current?.abort()
    const controller = new AbortController()
    pollAbortRef.current = controller
    return controller
  }

  const previewMutation = useMutation({
    mutationFn: (request: FlowStatePreviewRequest) => previewFlowStateProfiles(request),
    onSuccess: ({ data }) => setPreview(data),
    onError: () => setPreview(null),
  })

  const loadPreview = (payload: FlowStatePreviewRequest, page: number) => {
    previewMutation.reset()
    setPreview(null)
    setPreviewPage(page)
    previewMutation.mutate({
      ...payload,
      limit: PREVIEW_PAGE_SIZE,
      offset: page * PREVIEW_PAGE_SIZE,
    })
  }

  const generateMutation = useMutation({
    mutationFn: async (payload: GenerateFlowStateRequest & SendFlowStateRequest) => {
      setProgress({
        stage: 'generate',
        status: 'running',
        total: 0,
        succeeded: 0,
        failed: 0,
      })
      const controller = createPollController()
      const { data } = await generateFlowStateProfiles(payload)
      const result = await waitForJob(data.job_id, onProgress, {
        signal: controller.signal,
      })
      if (result.status === 'failed') throw new Error(result.error || 'Generate job failed')
      loadPreview(payload, 0)
      return result
    },
  })

  const sendMutation = useMutation({
    mutationFn: async (payload: SendFlowStateRequest) => {
      setProgress({
        stage: 'send',
        status: 'running',
        total: 0,
        succeeded: 0,
        failed: 0,
      })
      const controller = createPollController()
      const { data } = await sendFlowStateEmails(payload)
      const result = await waitForJob(data.job_id, onProgress, {
        signal: controller.signal,
      })
      if (result.status === 'failed') throw new Error(result.error || 'Send job failed')
      setSendResult({
        sent: result.succeeded,
        failed: result.failed,
        total: result.total,
      })
      loadPreview(payload, previewPage)
      return result
    },
  })

  const allUsersMutation = useMutation({
    mutationFn: async ({ reporting_month, dry_run }: { reporting_month: string; dry_run?: boolean }) => {
      const { data } = await generateAndSendFlowStateEmails({
        reporting_month,
        dry_run,
        resend_failed: true,
      })
      return { ...data, dry_run, reporting_month }
    },
    onSuccess: async (data) => {
      setAllUsersQueuedMessage(
        data.dry_run ? 'Dry-run regenerate and send job queued.' : 'Regenerate and send job queued.'
      )
      setPendingAllUsersEnqueueMonth(data.reporting_month)
      await queryClient.invalidateQueries({
        queryKey: [FLOW_STATE_MONTH_QUERY_KEY, data.reporting_month],
      })
    },
  })

  const allUsersSendMutation = useMutation({
    mutationFn: async ({ reporting_month, dry_run }: { reporting_month: string; dry_run?: boolean }) => {
      const { data } = await sendFlowStateEmails({
        reporting_month,
        all_users: true,
        dry_run,
        resend_failed: true,
      })
      return { ...data, dry_run, reporting_month }
    },
    onSuccess: async (data) => {
      setAllUsersQueuedMessage(data.dry_run ? 'Dry-run send-existing job queued.' : 'Send-existing job queued.')
      setPendingAllUsersEnqueueMonth(data.reporting_month)
      await queryClient.invalidateQueries({
        queryKey: [FLOW_STATE_MONTH_QUERY_KEY, data.reporting_month],
      })
    },
  })

  if (!isInternalAdmin) {
    return <Navigate to={FEATURE_PATH_FALLBACK} replace />
  }

  const isLocalMutationRunning =
    generateMutation.isPending || sendMutation.isPending || allUsersMutation.isPending || allUsersSendMutation.isPending
  const isAllUsersActionLocked = mode === 'all' && Boolean(activeAllUsersJob)
  const isAllUsersEnqueuePending = mode === 'all' && pendingAllUsersEnqueueMonth === reportingDate
  const isRunning = isLocalMutationRunning || isAllUsersActionLocked
  const latestAllUsersFailure =
    mode === 'all' && !activeAllUsersJob && monthSummary?.latest_job?.status === 'failed'
      ? new Error(monthSummary.latest_job.error || 'The latest all-users Flow State job failed.')
      : null
  const currentError =
    generateMutation.error ||
    sendMutation.error ||
    allUsersMutation.error ||
    allUsersSendMutation.error ||
    previewMutation.error ||
    latestAllUsersFailure ||
    monthSummaryQuery.error

  const resetMutations = () => {
    generateMutation.reset()
    sendMutation.reset()
    allUsersMutation.reset()
    allUsersSendMutation.reset()
    previewMutation.reset()
  }

  const submitGenerate = async () => {
    const payload = await resolveAudiencePayload()
    if (!payload) return
    setValidationError(null)
    clearAudienceResults()
    resetMutations()
    generateMutation.mutate(payload)
  }

  const submitSend = async () => {
    const payload = await resolveAudiencePayload()
    if (!payload) return
    setValidationError(null)
    setSendResult(null)
    resetMutations()
    sendMutation.mutate(payload)
  }

  const validateReportingMonth = async () => {
    const valid = await trigger('reportingMonth')
    if (!valid) {
      setValidationError(getFieldState('reportingMonth').error?.message || 'Choose a reporting month.')
      return false
    }
    return true
  }

  const submitAllUsersSendExisting = async (dryRun = false) => {
    if (!(await validateReportingMonth())) return
    setValidationError(null)
    setSendResult(null)
    setAllUsersQueuedMessage(null)
    resetMutations()
    allUsersSendMutation.mutate({
      reporting_month: reportingDate,
      dry_run: dryRun,
    })
  }

  const submitAllUsersRegenerateSend = async (dryRun = false) => {
    if (!(await validateReportingMonth())) return
    setValidationError(null)
    setSendResult(null)
    setAllUsersQueuedMessage(null)
    resetMutations()
    if (dryRun) {
      allUsersMutation.mutate({
        reporting_month: reportingDate,
        dry_run: true,
      })
      return
    }
    setPendingAllUsersMonth(reportingDate)
    setAllUsersConfirmValue('')
    setAllUsersConfirmOpen(true)
  }

  const refreshPreviewPage = async (page: number) => {
    const payload = await resolveAudiencePayload()
    if (!payload) return
    setValidationError(null)
    loadPreview(payload, page)
  }

  const displayedProgress = mode === 'all' ? allUsersProgress : progress
  const isAllUsersSummaryLoading = mode === 'all' && (monthSummaryQuery.isLoading || !monthSummary)
  const sendExistingTitle = isAllUsersSummaryLoading ? (
    <span className={styles.buttonSkeleton} aria-hidden="true" />
  ) : (
    `Send ${allUsersSendableCount} unsent ${profileNoun(allUsersSendableCount)}`
  )
  const dryRunSendExistingTitle = isAllUsersSummaryLoading
    ? 'Loading generated profile count...'
    : `Dry run send ${allUsersSendableCount} unsent ${profileNoun(allUsersSendableCount)}`
  const disableAllUsersRegenerate =
    isLocalMutationRunning ||
    isAllUsersActionLocked ||
    isAllUsersEnqueuePending ||
    isAllUsersSummaryLoading ||
    monthSummary?.can_regenerate_send === false
  const disableAllUsersSendExisting =
    isLocalMutationRunning ||
    isAllUsersActionLocked ||
    isAllUsersEnqueuePending ||
    isAllUsersSummaryLoading ||
    monthSummary?.can_send_existing !== true

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Flow State</title>
      </Helmet>
      <div className={clsx(baseStyles.container, styles.container)}>
        <div className={clsx(baseStyles.tabTitle, baseStyles.pageTabTitle)}>
          <h6>Flow State</h6>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <div className={styles.modeTabs} aria-label="Flow State audience">
              {(['emails', 'domain', 'all'] as AudienceMode[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  disabled={isLocalMutationRunning}
                  aria-pressed={mode === value}
                  className={clsx(styles.modeTab, mode === value && styles.modeTabActive)}
                  onClick={() => {
                    setMode(value)
                    storeAudienceMode(value)
                    setValue('audienceMode', value)
                    clearAudienceResults()
                    setValidationError(null)
                    resetMutations()
                  }}
                >
                  {value === 'emails' ? 'Emails' : value === 'domain' ? 'Domain' : 'Everyone'}
                </button>
              ))}
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="flow-state-month">
                  Reporting month
                </label>
                <Input
                  id="flow-state-month"
                  className={styles.input}
                  type="month"
                  max={firstOfPreviousMonth()}
                  value={reportingMonth}
                  onInput={(value) => setFormValue('reportingMonth', value)}
                  isDisabled={isLocalMutationRunning}
                />
              </div>
            </div>

            {mode === 'emails' && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="flow-state-emails">
                  Email addresses
                </label>
                <TextArea
                  id="flow-state-emails"
                  className={styles.textarea}
                  value={emailsText}
                  onInput={(value) => setFormValue('emailsText', value)}
                  placeholder="user@example.com, teammate@example.com"
                  isDisabled={isLocalMutationRunning}
                />
              </div>
            )}

            {mode === 'domain' && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="flow-state-domain">
                  Email domain
                </label>
                <Input
                  id="flow-state-domain"
                  className={styles.input}
                  type="text"
                  value={domainText}
                  onInput={(value) => setFormValue('domainText', value)}
                  placeholder="example.com"
                  isDisabled={isLocalMutationRunning}
                />
              </div>
            )}

            {mode === 'all' && (
              <div className={styles.warningBox}>
                Starts one backend-owned job for every eligible user with at least 500 dictated words in the selected
                month.
              </div>
            )}

            {displayedProgress && (isRunning || mode === 'all') && <ProgressBlock progress={displayedProgress} />}

            {validationError && (
              <div className={styles.errorBanner} role="alert">
                {validationError}
              </div>
            )}
            {currentError && (
              <div className={styles.errorBanner} role="alert">
                {errorMessage(currentError)}
              </div>
            )}
            {sendResult && (
              <div className={styles.successBanner} role="status" aria-live="polite">
                {sendResult.total === 0 ? (
                  'No profiles were eligible to send. They may already have been sent, or none were generated for this audience.'
                ) : (
                  <>
                    Sent {sendResult.sent} of {sendResult.total} Flow State events.
                    {sendResult.failed > 0 && <span> {sendResult.failed} failed.</span>}
                  </>
                )}
              </div>
            )}
            {allUsersQueuedMessage && (
              <div className={styles.successBanner} role="status" aria-live="polite">
                {allUsersQueuedMessage}
              </div>
            )}

            <div className={styles.actionBar}>
              {mode === 'all' && (
                <details className={styles.dryRunMenu}>
                  <summary>Dry run</summary>
                  <div className={styles.dryRunMenuItems}>
                    <button
                      type="button"
                      onClick={() => submitAllUsersRegenerateSend(true)}
                      disabled={disableAllUsersRegenerate}
                    >
                      Dry run regenerate & send to everyone
                    </button>
                    <button
                      type="button"
                      onClick={() => submitAllUsersSendExisting(true)}
                      disabled={disableAllUsersSendExisting}
                    >
                      {dryRunSendExistingTitle}
                    </button>
                  </div>
                </details>
              )}
              <div className={styles.submitRow}>
                {mode === 'all' ? (
                  <>
                    <Button
                      title="Regenerate & send to everyone"
                      type="destructive"
                      onClick={() => void submitAllUsersRegenerateSend()}
                      isLoading={allUsersMutation.isPending}
                      isDisabled={disableAllUsersRegenerate}
                    />
                    <Button
                      title={sendExistingTitle}
                      type="light"
                      onClick={() => void submitAllUsersSendExisting()}
                      isLoading={allUsersSendMutation.isPending}
                      isDisabled={disableAllUsersSendExisting}
                      ariaLabel={isAllUsersSummaryLoading ? 'Loading generated profile count' : undefined}
                    />
                  </>
                ) : (
                  <Button
                    title="Generate"
                    type="dark"
                    onClick={submitGenerate}
                    isLoading={generateMutation.isPending}
                    isDisabled={sendMutation.isPending || previewMutation.isPending}
                  />
                )}
                {mode !== 'all' && (
                  <Button
                    title="Send generated profiles"
                    type="light"
                    onClick={submitSend}
                    isLoading={sendMutation.isPending}
                    isDisabled={!preview || generateMutation.isPending}
                  />
                )}
              </div>
            </div>
          </section>

          {mode !== 'all' && preview && (
            <PreviewTable
              preview={preview}
              page={previewPage}
              onPage={refreshPreviewPage}
              isLoading={previewMutation.isPending}
            />
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={allUsersConfirmOpen}
        title="Send Flow State to everyone"
        message={
          <div className={styles.confirmBody}>
            <p>
              This will generate and send Flow State emails to every eligible user for {pendingAllUsersMonth}. Type{' '}
              <strong>{ALL_USERS_CONFIRM_TEXT}</strong> to continue.
            </p>
            <Input
              id="flow-state-all-users-confirm"
              type="text"
              value={allUsersConfirmValue}
              onInput={setAllUsersConfirmValue}
              placeholder={ALL_USERS_CONFIRM_TEXT}
            />
          </div>
        }
        warning="This job runs in the backend and may take longer than this page stays open."
        confirmLabel="Generate and send"
        isConfirming={allUsersMutation.isPending}
        isConfirmDisabled={allUsersConfirmValue !== ALL_USERS_CONFIRM_TEXT}
        onCancel={() => {
          setAllUsersConfirmOpen(false)
          setPendingAllUsersMonth(null)
        }}
        onConfirm={() => {
          if (!pendingAllUsersMonth) return
          setAllUsersConfirmOpen(false)
          allUsersMutation.mutate({ reporting_month: pendingAllUsersMonth })
        }}
      />
    </>
  )
}

export default FlowStatePage
